import { Icons } from '@/utils';
import { NButton, NIcon, NText, NTooltip } from 'naive-ui';
import type { Size, Type } from 'naive-ui/es/button/src/interface';
import { computed, defineComponent, type PropType } from 'vue';

export const AppButtonIcon = defineComponent({
	name: 'AppButtonIcon',

	props: {
		icon: {
			type: Object as PropType<any>,
			required: true,
		},
		loading: {
			type: Boolean,
			default: false,
		},
		hasError: {
			type: Boolean,
			default: false,
		},
		errorMessage: {
			type: String,
			default: '',
		},
		type: {
			type: String as PropType<Type>,
			default: 'default',
		},
		size: {
			type: String as PropType<Size>,
			default: 'medium',
		},
		iconSize: {
			type: String as PropType<Size>,
			default: '18',
		},
		successMessage: {
			type: String,
			default: '',
		},
		hasSuccess: {
			type: Boolean,
			default: false,
		},
		tooltipMessage: {
			type: String,
			default: '',
		},
		tooltipPosition: {
			type: String,
			default: 'top',
		},
		disabled: {
			type: Boolean,
			default: false,
		},
		attrHref: {
			type: String,
			default: '',
		},
		attrTarget: {
			type: String,
			default: '',
		},

		attrType: {
			type: String as PropType<'button' | 'submit' | 'reset'>,
			default: undefined,
		},

		tag: {
			type: String as PropType<keyof HTMLElementTagNameMap>,
			default: 'button',
		},

		secondary: {
			type: Boolean,
			default: true,
		},
	},

	emits: ['click'],

	setup(props, { emit, slots }) {
		const handleClick = (e: MouseEvent) => {
			emit('click', e);
		};

		const currentType = computed(() => {
			if (props.hasError) return 'error';
			if (props.hasSuccess) return 'success';
			return props.type;
		});

		const renderButtonContent = () => ({
			icon: () => {
				if (props.hasError) {
					return <NIcon size={props.iconSize} component={Icons.Warning} />;
				}
				if (props.hasSuccess) {
					return <NIcon size={props.iconSize} component={Icons.Done} />;
				}
				return <NIcon size={props.iconSize} component={props.icon} />;
			},
			default: () => {
				if (props.hasError) {
					return <NText>{props.errorMessage}</NText>;
				}
				if (props.hasSuccess) {
					return <NText>{props.successMessage}</NText>;
				}

				return slots.default?.();
			},
		});

		const renderButton = () => (
			<NButton
				//@ts-expect-error problema de type
				disabled={props.disabled}
				href={props.attrHref}
				target={props.attrTarget}
				tag={props.tag}
				attrType={props.attrType}
				type={currentType.value}
				size={props.size}
				loading={props.loading}
				onClick={handleClick}
				secondary={props.secondary}
			>
				{renderButtonContent()}
			</NButton>
		);

		return () => {
			if (!props.tooltipMessage) {
				return renderButton();
			}

			return (
				<NTooltip disabled={!props.tooltipMessage}>
					{{
						trigger: renderButton,
						default: () => <span>{props.tooltipMessage}</span>,
					}}
				</NTooltip>
			);
		};
	},
});
